import type { LieType } from "../board/types";
import type { VerseDef } from "./types";

/**
 * Named abilities for Proclaim.
 * Hover/reminder copy: plain English first, numbers second, short.
 * Lies have levels III (full) → II → I → silent.
 */
export type KeywordId =
  | "answer"
  | "soften"
  | "innermost"
  | "mightiest"
  | "discern"
  | "strong-vs"
  | "abide"
  | "guard"
  | "still-heart"
  | "contest"
  | "joy"
  | "advance"
  | "restore"
  | "layer-slam"
  | "layer-chip"
  | "still"
  | "redeem"
  | "empower"
  | "restore-keep"
  | "gather"
  | "renew"
  | "refuge"
  | "recall"
  | "haste"
  | "lay-aside"
  | "name"
  | "cast-down"
  | "wipe-confusion";

export interface KeywordCatalogEntry {
  id: KeywordId;
  name: string;
  summary: string;
  reminder: string;
}

export const KEYWORD_CATALOG: Record<KeywordId, KeywordCatalogEntry> = {
  answer: {
    id: "answer",
    name: "Answer",
    summary: "Base steps of damage to one lie.",
    reminder:
      "Answer is the base weaken (I = one step: III→II→I→gone). Meets and perfect aim add on top when they apply.",
  },
  soften: {
    id: "soften",
    name: "Soften",
    summary: "Always weaken one lie by 1 — sets up Redeem.",
    reminder:
      "Drag onto one lie. Always one step (I), any type — no Meets bonus. Use this to crack a full-strength lie so Redeem can turn it into a Prayer.",
  },
  innermost: {
    id: "innermost",
    name: "Aims deepest",
    summary: "Auto-play prefers the deepest threatened layer.",
    reminder:
      "Targets the deepest layer that still has a lie (often Will). On short snipes only, a perfect deep hit adds Aim I (a third step).",
  },
  mightiest: {
    id: "mightiest",
    name: "Aims strongest",
    summary: "Auto-play prefers the strongest lie.",
    reminder:
      "Targets one of the highest-level lies. On short snipes only, a perfect hit adds Aim I (a third step).",
  },
  discern: {
    id: "discern",
    name: "Prefers",
    summary: "Auto-play hunts listed lie types first.",
    reminder:
      "When those types are on the board, auto-play aims at them first. This only chooses the target — damage is Answer + Meets.",
  },
  "strong-vs": {
    id: "strong-vs",
    name: "Meets",
    summary: "Bonus steps when the lie’s type matches.",
    reminder:
      "Added on top of Answer when the type matches. Meets I = one extra step (Answer I + Meets I → two steps total).",
  },
  abide: {
    id: "abide",
    name: "Abide",
    summary: "Place a Prayer that stays on the board.",
    reminder:
      "Occupies a slot (you can replace your own). Will is always open; Mind/Heart need a foothold nearby. Contest only reaches adjacent columns — two wings with an empty center trap a lie. A packed row (no empty slot) frays everyone on it.",
  },
  guard: {
    id: "guard",
    name: "Guard",
    summary: "Sturdier Prayer; harder for lies to break.",
    reminder:
      "This Prayer resists wear better and eases how hard darkness presses inward.",
  },
  "still-heart": {
    id: "still-heart",
    name: "Still",
    summary: "Nudge a layer toward Truth.",
    reminder:
      "While present, slowly pulls this layer’s meter toward Truth (light).",
  },
  contest: {
    id: "contest",
    name: "Contest",
    summary: "Wear down an adjacent foe.",
    reminder:
      "Over time, damages an opposing presence in this column or the next one over. The far wing is out of reach — an empty center can trap a lie between two keeps. A packed row frays everyone on it.",
  },
  joy: {
    id: "joy",
    name: "Joy",
    summary: "For a time, slowly contest matching lies board-wide.",
    reminder:
      "Soft multi-target pressure: wears Fear (or listed types) over several seconds, usually about one step each — not a wipe.",
  },
  advance: {
    id: "advance",
    name: "Path",
    summary: "Prayer can step outward toward Mind.",
    reminder:
      "After a wind-up, steps one layer out (Will→Heart→Mind) if the way is fairly clear. Crowded layers slow or block the path.",
  },
  restore: {
    id: "restore",
    name: "Restore",
    summary: "Mend Secret Place integrity.",
    reminder:
      "Heals Secret Place integrity. Bonus if any Despair is on the board.",
  },
  "layer-slam": {
    id: "layer-slam",
    name: "Peace over",
    summary: "Pull one whole layer toward Truth.",
    reminder:
      "Shoves one layer’s meter hard toward Truth. Drag chooses the layer when allowed.",
  },
  "layer-chip": {
    id: "layer-chip",
    name: "Chip layer",
    summary: "Weaken every matching lie on one layer.",
    reminder:
      "Hits one layer only: each matching lie loses a fixed number of steps (usually 1). Best on crowded rows; weak on empty ones.",
  },
  still: {
    id: "still",
    name: "Still",
    summary: "Slow a lie’s advance or spread.",
    reminder:
      "While active, the lie fills its push/spread bar much more slowly. Layer Still hits every lie on that layer.",
  },
  redeem: {
    id: "redeem",
    name: "Redeem",
    summary: "Turn a weakened lie into a Prayer.",
    reminder:
      "Only works on a lie already below full strength (chip it first). The lie becomes your Prayer in that slot.",
  },
  empower: {
    id: "empower",
    name: "Empower",
    summary: "Next Answer or Chip hits harder.",
    reminder:
      "Your next Answer or Chip gains +1 step. Eligible cards glow gold. Speak this first, then strike.",
  },
  "restore-keep": {
    id: "restore-keep",
    name: "Restore Keep",
    summary: "Fully mend a damaged Prayer.",
    reminder:
      "Drag onto a Prayer below full strength to restore it. If none need help, the card stays in hand.",
  },
  gather: {
    id: "gather",
    name: "Gather",
    summary: "Feed residual (I) Prayers into a chosen keep.",
    reminder:
      "Drag onto a Prayer below its max. Other I Prayers are consumed to raise it (+1 each), up to that keep’s designed max — never past it. No valid host or no other I’s: card stays in hand.",
  },
  renew: {
    id: "renew",
    name: "Renew",
    summary: "Fortify a restored Prayer against wear; ease neighbors.",
    reminder:
      "After Restore Keep: that Prayer takes less wear for a short time, and nearby Prayers on the same layer clear their wear.",
  },
  refuge: {
    id: "refuge",
    name: "Refuge",
    summary: "On Will: slowly heal the Secret Place.",
    reminder:
      "While this Prayer holds Will, Integrity regenerates over time.",
  },
  recall: {
    id: "recall",
    name: "Recall",
    summary: "Draw extra verses into hand.",
    reminder:
      "After you play this, extra verses deal into your hand (may briefly go over the usual hand size).",
  },
  haste: {
    id: "haste",
    name: "Haste",
    summary: "Next proclaim(s) finish faster.",
    reminder:
      "Your next verse(s) take less speak time, so you can answer again sooner.",
  },
  "lay-aside": {
    id: "lay-aside",
    name: "Lay aside",
    summary: "Put a hand card on the used pile and draw.",
    reminder:
      "Drop onto another card in hand to set it aside (or the game picks a situational brick). It returns when the used pile shuffles. The speak time is the cost.",
  },
  name: {
    id: "name",
    name: "Name",
    summary: "Reveal Dogma’s demanded color; Still it briefly.",
    reminder:
      "The claim stays foggy. The type chip lights so you know which Word peels. Does not peel by itself unless this verse’s color is the demand.",
  },
  "cast-down": {
    id: "cast-down",
    name: "Cast down",
    summary: "Finish a Dogma core if it is exposed.",
    reminder:
      "Only the last shell. Earlier shells need their demanded color first. A rider — this card still does its other job when Settled is not on the board.",
  },
  "wipe-confusion": {
    id: "wipe-confusion",
    name: "Cast down confusion",
    summary: "Silence every Confusion on the board.",
    reminder:
      "Hits only twisted leftovers (Confusion II and Tangled Echo I). Ordinary residuals are left alone.",
  },
};

export interface KeywordInstance {
  id: KeywordId;
  label: string;
  reminder: string;
}

function typeNames(types: LieType[]): string {
  return types.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" / ");
}

/** Intensity steps as Roman numerals (same language as lie levels). */
function stepRoman(n: number): string {
  if (n >= 3) return "III";
  if (n === 2) return "II";
  return "I";
}

/** Types this Answer hits harder against (explicit strongVs, else category default). */
function answerMeetTypes(def: VerseDef): LieType[] {
  if (def.strongVs?.length) return [...def.strongVs];
  // Category default: Faith↔Fear/Doubt, Peace↔Despair, Wisdom↔Doubt (matches meetsLie)
  if (def.category === "faith") return ["fear", "doubt"];
  if (def.category === "peace") return ["despair"];
  if (def.category === "wisdom") return ["doubt"];
  return [];
}

/**
 * Damage as additive stacks: Answer I (base) + Meets I (type) [+ Aim I on micro].
 * Reminders stay short for inline “In play” paragraphs.
 */
function pushAnswerKeyword(
  list: KeywordInstance[],
  def: VerseDef,
  _opts?: { withStill?: boolean },
): void {
  const base = Math.max(1, def.answerLevels ?? 1);
  const tier = def.snipeTier ?? "standard";
  const meet = answerMeetTypes(def);
  const meetLabel = meet.length ? typeNames(meet) : "";

  if (tier === "soften") {
    list.push({
      id: "soften",
      label: `Soften ${stepRoman(base)}`,
      reminder:
        "Always one step, any type — no Meets. Cracks a full III so Redeem can fire.",
    });
    return;
  }

  // Base Answer always first
  list.push({
    id: "answer",
    label: `Answer ${stepRoman(base)}`,
    reminder:
      base === 1
        ? "Base weaken: one step on the target (III→II→I→gone)."
        : `Base weaken: ${stepRoman(base)} steps on the target (III→II→I→gone).`,
  });

  // Meets is additive on type match
  if (meetLabel) {
    const meetSteps = 1;
    list.push({
      id: "strong-vs",
      label: `Meets ${stepRoman(meetSteps)} · ${meetLabel}`,
      reminder:
        `+${stepRoman(meetSteps)} on type match (with Answer = ${stepRoman(base + meetSteps)} total)` +
        (tier === "micro" ? "." : "; not a full III wipe alone."),
    });
  }
}

/**
 * How auto-play chooses a target — not a second damage bonus
 * (except micro snipes, noted in reminder).
 */
function pushAimKeyword(list: KeywordInstance[], def: VerseDef): void {
  const sel = def.selector ?? "strongestLie";
  const tier = def.snipeTier ?? "standard";
  const microAim = tier === "micro";

  if (sel === "innermostLie") {
    list.push({
      id: "innermost",
      label: microAim ? "Aim I · deepest" : KEYWORD_CATALOG.innermost.name,
      reminder: microAim
        ? "Auto prefers the deepest threatened layer; perfect hit there adds Aim I."
        : "Auto prefers the deepest threatened layer (often Will) — aim only, no extra damage.",
    });
    return;
  }

  if (sel === "strongestLie") {
    list.push({
      id: "mightiest",
      label: microAim ? "Aim I · strongest" : KEYWORD_CATALOG.mightiest.name,
      reminder: microAim
        ? "Auto prefers the strongest lie; perfect hit there adds Aim I."
        : "Auto prefers the strongest lie — aim only, no extra damage.",
    });
    return;
  }

  if (sel === "typePriority") {
    const pts = def.priorityTypes ?? [];
    const types = pts.length ? typeNames(pts) : "preferred types";
    const hunt =
      pts.length > 1
        ? pts.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" → ")
        : types;
    list.push({
      id: "discern",
      label: microAim ? `Aim I · ${hunt}` : `Hunts ${hunt}`,
      reminder: microAim
        ? "Auto picks targets in that order; perfect hit adds Aim I."
        : "Auto picks targets in that order — aim only; damage is Answer + Meets.",
    });
  }
}

/** Derive keyword pills for a verse (order is intentional for reading). */
export function keywordsForVerse(def: VerseDef): KeywordInstance[] {
  const list: KeywordInstance[] = [];

  if (def.kind === "placeable" && def.placeable) {
    list.push({
      id: "abide",
      label: `Abide ${["", "I", "II", "III"][def.placeable.level] ?? def.placeable.level}`,
      reminder: KEYWORD_CATALOG.abide.reminder,
    });
    const v = def.placeable.verbs;
    if (v.guard) {
      list.push({
        id: "guard",
        label: KEYWORD_CATALOG.guard.name,
        reminder: KEYWORD_CATALOG.guard.reminder,
      });
    }
    if (v.pressure !== undefined && v.pressure < 0) {
      const rate = Math.abs(v.pressure);
      list.push({
        id: "still-heart",
        label: `Still ${rate.toFixed(0)}`,
        reminder: `Pulls this layer toward Truth (~${rate}/s) while the Prayer remains.`,
      });
    }
    if (v.contest) {
      if (v.contestNextLayer) {
        list.push({
          id: "contest",
          label: `Contest ahead ${v.contest}`,
          reminder: `Wears down an adjacent foe on the next layer outward (rate ~${v.contest}) — same column or one over. Far wing is out of reach.`,
        });
      } else {
        list.push({
          id: "contest",
          label: `Contest ${v.contest}`,
          reminder: `Wears down an adjacent foe on this layer (rate ~${v.contest}) — same column or one over. Far wing is out of reach.`,
        });
      }
    }
    if (v.advanceAfter) {
      list.push({
        id: "advance",
        label: `Path ${v.advanceAfter.toFixed(1)}s`,
        reminder: `After ~${v.advanceAfter.toFixed(1)}s, steps one layer outward if the way is fairly clear.`,
      });
    }
    if (v.refugeRegen) {
      list.push({
        id: "refuge",
        label: `Refuge +${v.refugeRegen.toFixed(1)}/s`,
        reminder: `On Will: restores ~${v.refugeRegen.toFixed(1)} Integrity/s while this Prayer remains.`,
      });
    }
  }

  if (def.kind === "instant") {
    if (def.selector === "empower") {
      const n = def.empowerCharges ?? 1;
      const bonus = def.empowerBonusSteps ?? 1;
      list.push({
        id: "empower",
        label: n > 1 ? `Empower ×${n} (+${bonus})` : `Empower +${bonus}`,
        reminder: `Next Answer or Chip gains +${bonus} step${bonus === 1 ? "" : "s"}. Eligible cards glow. Speak this, then play the follow-up.`,
      });
    }

    if (def.selector === "gatherPrayers") {
      list.push({
        id: "gather",
        label: KEYWORD_CATALOG.gather.name,
        reminder: KEYWORD_CATALOG.gather.reminder,
      });
    }

    if (def.selector === "restorePrayer") {
      list.push({
        id: "restore-keep",
        label: KEYWORD_CATALOG["restore-keep"].name,
        reminder: KEYWORD_CATALOG["restore-keep"].reminder,
      });
      if (def.fortifyAfterRestore || def.mendAdjacent) {
        const dur = def.fortifyAfterRestore?.duration;
        const fac = def.fortifyAfterRestore?.wearFactor ?? 0.5;
        const pct = Math.round((1 - fac) * 100);
        const parts: string[] = [];
        if (dur && dur > 0) {
          parts.push(
            `fortifies the restored Prayer for ${dur.toFixed(0)}s (−${pct}% wear)`,
          );
        }
        if (def.mendAdjacent) {
          const lv = def.mendAdjacent.levels ?? 0;
          parts.push(
            lv > 0
              ? `eases nearby Prayers on that layer (clear wear, +${lv} level)`
              : "eases nearby Prayers on that layer (clear wear)",
          );
        }
        list.push({
          id: "renew",
          label: dur ? `Renew ${dur.toFixed(0)}s` : "Renew",
          reminder: parts.join("; ") + ".",
        });
      }
    }

    if (def.selector === "layerSlam") {
      list.push({
        id: "layer-slam",
        label: def.tiltSlam
          ? `Peace over ${Math.abs(def.tiltSlam)}`
          : KEYWORD_CATALOG["layer-slam"].name,
        reminder: KEYWORD_CATALOG["layer-slam"].reminder,
      });
    }

    if (def.selector === "layerChip") {
      const steps = def.answerLevels ?? 1;
      const lock = def.lockLayer
        ? def.lockLayer.charAt(0).toUpperCase() + def.lockLayer.slice(1)
        : null;
      const types = def.chipOnlyTypes?.length
        ? def.chipOnlyTypes
            .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
            .join(" / ")
        : "all lies";
      const where = lock
        ? `Only ${lock}: each ${types === "all lies" ? "lie" : types} loses −${steps}.`
        : `One layer (drag to choose): each of ${types} loses −${steps}.`;
      list.push({
        id: "layer-chip",
        label: lock ? `Chip ${lock} −${steps}` : `Chip layer −${steps}`,
        reminder: `${where} Best on crowded rows.`,
      });
    }

    if (def.selector === "slowLie" || def.selector === "slowLayer") {
      const secs = (def.slowDuration ?? 5).toFixed(0);
      list.push({
        id: "still",
        label:
          def.selector === "slowLayer" ? `Still layer ${secs}s` : `Still ${secs}s`,
        reminder:
          def.selector === "slowLayer"
            ? `For ${secs}s, lies on that layer advance/spread much more slowly.`
            : `For ${secs}s, that lie advances/spreads much more slowly.`,
      });
    } else if (
      def.slowDuration &&
      def.slowDuration > 0 &&
      def.selector === "layerChip"
    ) {
      const secs = def.slowDuration.toFixed(0);
      list.push({
        id: "still",
        label: `Still layer ${secs}s`,
        reminder: `Remaining lies on the row advance/spread slowly for ${secs}s.`,
      });
    }

    if (def.selector === "convertLie") {
      const bonus = def.convertPrayerBonus ?? 0;
      const strength = bonus >= 1 ? "Strong Redeem" : "Weak Redeem";
      const how =
        bonus >= 1
          ? "Prayer is one step stronger than the lie when possible."
          : "Prayer matches the lie’s strength.";
      const prefer =
        def.priorityTypes?.length
          ? ` Prefers ${typeNames(def.priorityTypes)} when choosing.`
          : "";
      list.push({
        id: "redeem",
        label: def.convertPrayerName
          ? `${strength} → ${def.convertPrayerName}`
          : strength,
        reminder: `Only on an already-weakened lie (not full III). ${how} Turns it into your Prayer.${prefer}`,
      });
    }

    // Single-target Answer / Soften / Still+Answer — one damage pill, then aim
    const isSingleAnswer =
      !!def.answerLevels &&
      (def.selector === "strongestLie" ||
        def.selector === "innermostLie" ||
        def.selector === "typePriority" ||
        def.selector === "slowLie" ||
        !def.selector);

    if (isSingleAnswer) {
      const stillOnAnswer =
        !!def.slowDuration &&
        def.slowDuration > 0 &&
        def.selector !== "slowLie"; // slowLie already has its own Still pill
      pushAnswerKeyword(list, def, { withStill: stillOnAnswer });
      // Aim only when it describes targeting (not on pure slowLie drag)
      if (def.selector !== "slowLie") {
        pushAimKeyword(list, def);
      }
      if (stillOnAnswer) {
        const secs = def.slowDuration!.toFixed(0);
        list.push({
          id: "still",
          label: `Still ${secs}s`,
          reminder: `If the lie remains, it advances/spreads slowly for ${secs}s.`,
        });
      }
    }

    if (def.handDraw && def.handDraw > 0) {
      list.push({
        id: "recall",
        label: `Recall +${def.handDraw}`,
        reminder: `After play, draw ${def.handDraw} extra verse${def.handDraw === 1 ? "" : "s"} into hand.`,
      });
    }
    if (def.speakHaste) {
      const n = def.speakHaste.charges ?? 1;
      const pct = Math.round((1 - def.speakHaste.factor) * 100);
      list.push({
        id: "haste",
        label: n > 1 ? `Haste ×${n} (−${pct}%)` : `Haste −${pct}%`,
        reminder: `Your next ${n} proclaim${n === 1 ? "" : "s"} take ${pct}% less speak time.`,
      });
    }
    if (def.restoreIntegrity) {
      list.push({
        id: "restore",
        label: `Restore ${def.restoreIntegrity}`,
        reminder: def.joyField
          ? `Heal ${def.restoreIntegrity} Integrity.`
          : `Heal ${def.restoreIntegrity} Integrity (+ more if Despair is on the board).`,
      });
    }
    if (def.joyField) {
      const jf = def.joyField;
      const secs = jf.duration.toFixed(0);
      const types = jf.onlyTypes?.length
        ? typeNames(jf.onlyTypes)
        : "Fear";
      const steps = jf.maxStepsPerLie ?? 1;
      list.push({
        id: "joy",
        label: `Joy ${secs}s · ${types}`,
        reminder: `For ${secs}s, slowly contest every ${types} lie on the board (~${steps} level step${steps === 1 ? "" : "s"} each if they stay under Joy). Mild Still while active.`,
      });
    }
    if (def.layAside) {
      list.push({
        id: "lay-aside",
        label: KEYWORD_CATALOG["lay-aside"].name,
        reminder: KEYWORD_CATALOG["lay-aside"].reminder,
      });
    }
    if (def.nameLie) {
      list.push({
        id: "name",
        label: KEYWORD_CATALOG.name.name,
        reminder: KEYWORD_CATALOG.name.reminder,
      });
    }
    if (def.wipeConfusion) {
      list.push({
        id: "wipe-confusion",
        label: KEYWORD_CATALOG["wipe-confusion"].name,
        reminder: KEYWORD_CATALOG["wipe-confusion"].reminder,
      });
    }
    if (def.castDown) {
      list.push({
        id: "cast-down",
        label: KEYWORD_CATALOG["cast-down"].name,
        reminder: KEYWORD_CATALOG["cast-down"].reminder,
      });
    }
  }

  // Meets for non-Answer tools only (Answers already push Meets above)
  const answerPushedMeets =
    !!def.answerLevels &&
    def.snipeTier !== "soften" &&
    (def.selector === "strongestLie" ||
      def.selector === "innermostLie" ||
      def.selector === "typePriority" ||
      def.selector === "slowLie" ||
      !def.selector);
  if (def.strongVs?.length && !answerPushedMeets) {
    // Chip / Redeem: type filter is not the Answer+Meets stack — skip misleading Meets
    if (def.selector !== "layerChip" && def.selector !== "convertLie") {
      const names = typeNames(def.strongVs);
      list.push({
        id: "strong-vs",
        label: `Meets I · ${names}`,
        reminder: `Bonus effectiveness against ${names}.`,
      });
    }
  }

  const seen = new Set<KeywordId>();
  return list.filter((k) => {
    if (seen.has(k.id)) return false;
    seen.add(k.id);
    return true;
  });
}
